/* global $ */
import React, { useState, useEffect } from "react";
import axiosInstance from "../axiosInstance";
import Swal from "sweetalert2";
import { useNavigate, useLocation } from "react-router-dom";
import Footer from "../components/Footer";

const GENDERS = ['Male', 'Female', 'Other'];
const STATUSES = ['active', 'inactive', 'blocked'];
const CONTACT_METHODS = ['Phone', 'WhatsApp', 'Email'];

function AddCustomer() {
    const navigate = useNavigate();
    const location = useLocation();
    const isEdit = location.state?.isEdit || false;
    const existingCustomer = location.state?.customer || null;

    const [isSaving, setIsSaving] = useState(false);
    const [loadingData, setLoadingData] = useState(isEdit);

    const [customerData, setCustomerData] = useState({
        name: '',
        email: '',
        phone: '',
        alternate_phone: '',
        dob: '',
        gender: '',
        country_id: '',
        state_id: '',
        city_id: '',
        address: '',
        passport_number: '',
        passport_expiry_date: '',
        preferred_contact_method: '',
        status: 'active',
        notes: '',
    });

    const [countries, setCountries] = useState([]);
    const [allStates, setAllStates] = useState([]);
    const [allCities, setAllCities] = useState([]);
    const [filteredStates, setFilteredStates] = useState([]);
    const [filteredCities, setFilteredCities] = useState([]);
    const [errors, setErrors] = useState({});

    // ─── Load Dropdowns ───────────────────────────────────────────────────────────

    useEffect(() => {
        loadDropdowns();
    }, []);

    const loadDropdowns = async () => {
        const safe = async (url, setter, label) => {
            try {
                const res = await axiosInstance.get(url);
                const raw = res.data;
                const list = Array.isArray(raw) ? raw
                    : Array.isArray(raw?.data) ? raw.data
                    : Array.isArray(raw?.rows) ? raw.rows
                    : Array.isArray(raw?.result) ? raw.result
                    : [];
                setter(list);
            } catch (err) {
                console.error(`❌ ${label} FAILED:`, err?.response?.status, url);
                setter([]);
            }
        };

        await Promise.all([
            safe('/api/country', setCountries,  'Countries'),
            safe('/api/state',    setAllStates,  'States'),
            safe('/api/cities',    setAllCities,  'Cities'),
        ]);
    };

    // ─── Prefill Edit ─────────────────────────────────────────────────────────────

    useEffect(() => {
        if (isEdit && existingCustomer) {
            setCustomerData({
                name: existingCustomer.name || '',
                email: existingCustomer.email || '',
                phone: existingCustomer.phone || '',
                alternate_phone: existingCustomer.alternate_phone || '',
                dob: existingCustomer.dob ? existingCustomer.dob.split('T')[0] : '',
                gender: existingCustomer.gender || '',
                country_id: existingCustomer.country_id || '',
                state_id: existingCustomer.state_id || '',
                city_id: existingCustomer.city_id || '',
                address: existingCustomer.address || '',
                passport_number: existingCustomer.passport_number || '',
                passport_expiry_date: existingCustomer.passport_expiry_date
                    ? existingCustomer.passport_expiry_date.split('T')[0] : '',
                preferred_contact_method: existingCustomer.preferred_contact_method || '',
                status: existingCustomer.status || 'active',
                notes: existingCustomer.notes || '',
            });
            setLoadingData(false);
        }
    }, [isEdit, existingCustomer]);

    // ─── Cascading: Country → State ───────────────────────────────────────────────

    useEffect(() => {
        if (customerData.country_id) {
            const filtered = allStates.filter(
                s => String(s.country_id) === String(customerData.country_id)
            );
            setFilteredStates(filtered);
            // Reset state & city only if country changed (not on initial edit load)
            if (!isEdit || loadingData === false) {
                const stateStillValid = filtered.some(
                    s => String(s.id) === String(customerData.state_id)
                );
                if (!stateStillValid) {
                    setCustomerData(prev => ({ ...prev, state_id: '', city_id: '' }));
                    setFilteredCities([]);
                }
            }
        } else {
            setFilteredStates([]);
            setFilteredCities([]);
        }
    }, [customerData.country_id, allStates]);

    // ─── Cascading: State → City ──────────────────────────────────────────────────

    useEffect(() => {
        if (customerData.state_id) {
            const filtered = allCities.filter(
                c => String(c.state_id) === String(customerData.state_id)
            );
            setFilteredCities(filtered);
            // Reset city only if state changed
            const cityStillValid = filtered.some(
                c => String(c.id) === String(customerData.city_id)
            );
            if (!cityStillValid) {
                setCustomerData(prev => ({ ...prev, city_id: '' }));
            }
        } else {
            setFilteredCities([]);
        }
    }, [customerData.state_id, allCities]);

    // ─── Handlers ────────────────────────────────────────────────────────────────

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCustomerData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: undefined }));
    };

    // ─── Validation ───────────────────────────────────────────────────────────────

    const validate = () => {
        const errs = {};
        if (!customerData.name.trim()) errs.name = 'Name is required';
        if (!customerData.phone.trim()) errs.phone = 'Phone is required';
        if (customerData.phone && !/^[0-9]{10}$/.test(customerData.phone.trim()))
            errs.phone = 'Enter a valid 10-digit phone number';
        if (customerData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerData.email))
            errs.email = 'Enter a valid email address';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // ─── Save ─────────────────────────────────────────────────────────────────────

    const toNull = (v) => (v === '' || v === undefined ? null : v);

    const saveCustomer = async (e) => {
        e.preventDefault();
        if (isSaving) return;
        if (!validate()) return;
        setIsSaving(true);

        try {
            const user_id = localStorage.getItem('user_id');
            const payload = {
                name: customerData.name.trim(),
                email: toNull(customerData.email),
                phone: customerData.phone.trim(),
                alternate_phone: toNull(customerData.alternate_phone),
                dob: toNull(customerData.dob),
                gender: toNull(customerData.gender),
                country_id: toNull(customerData.country_id),
                state_id: toNull(customerData.state_id),
                city_id: toNull(customerData.city_id),
                address: toNull(customerData.address),
                passport_number: toNull(customerData.passport_number),
                passport_expiry_date: toNull(customerData.passport_expiry_date),
                preferred_contact_method: toNull(customerData.preferred_contact_method),
                status: customerData.status,
                notes: toNull(customerData.notes),
                isactive: true,
                updated_by: user_id,
            };

            if (isEdit) {
                await axiosInstance.put(`/api/customer/${existingCustomer.id}`, payload);
                Swal.fire({ icon: 'success', title: 'Updated!', text: 'Customer updated successfully', timer: 1500, showConfirmButton: false });
                navigate(`/customer-details/${existingCustomer.id}`);
            } else {
                payload.created_by = user_id;
                const res = await axiosInstance.post('/api/customer', payload);
                const new_id = res.data?.data?.id || res.data?.id;
                Swal.fire({ icon: 'success', title: 'Saved!', text: 'Customer created successfully', timer: 1500, showConfirmButton: false });
                navigate(`/customer-details/${new_id}`);
            }
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Failed to save customer' });
        }
        setIsSaving(false);
    };

    const handleBack = () => {
        if (isEdit) navigate(`/customer-details/${existingCustomer.id}`);
        else navigate('/customers');
    };

    // ─── Loading ──────────────────────────────────────────────────────────────────

    if (loadingData) return (
        <div className="page-wrapper">
            <div className="content container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
            <Footer />
        </div>
    );

    return (
        <div>
            <div className="page-wrapper">
                <div className="content container-fluid">
                    <div className="card mb-0">
                        <div className="card-body">

                            {/* ── Page Header ── */}
                            <div className="page-header">
                                <div className="content-page-header d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center">
                                        <button type="button" className="btn btn-sm btn-outline-secondary me-3"
                                            onClick={handleBack}>
                                            <i className="fa fa-arrow-left me-1"></i> Back
                                        </button>
                                        <h5 className="mb-0">{isEdit ? 'Edit Customer' : 'Add Customer'}</h5>
                                    </div>
                                </div>
                            </div>

                            {/* Breadcrumb */}
                            <nav aria-label="breadcrumb" className="mb-4">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <span style={{ cursor: 'pointer', color: '#007bff' }} onClick={() => navigate('/customer')}>
                                            Customers
                                        </span>
                                    </li>
                                    {isEdit && (
                                        <li className="breadcrumb-item">
                                            <span style={{ cursor: 'pointer', color: '#007bff' }}
                                                onClick={() => navigate(`/customer-details/${existingCustomer.id}`)}>
                                                {existingCustomer?.name}
                                            </span>
                                        </li>
                                    )}
                                    <li className="breadcrumb-item active">
                                        {isEdit ? 'Edit' : 'Add Customer'}
                                    </li>
                                </ol>
                            </nav>

                            <form onSubmit={saveCustomer} noValidate>

                                {/* ── Personal Information ── */}
                                <h5 className="form-title">Personal Information</h5>
                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <label>Full Name <span className="text-danger">*</span></label>
                                        <input type="text"
                                            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                            name="name" value={customerData.name} onChange={handleChange}
                                            placeholder="Enter full name" />
                                        {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label>Phone <span className="text-danger">*</span> <small className="text-muted">(10 digits)</small></label>
                                        <input type="text"
                                            className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                                            name="phone" value={customerData.phone} onChange={handleChange}
                                            placeholder="9876543210" />
                                        {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label>Alternate Phone</label>
                                        <input type="text" className="form-control"
                                            name="alternate_phone" value={customerData.alternate_phone}
                                            onChange={handleChange} placeholder="Alternate phone number" />
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label>Email</label>
                                        <input type="email"
                                            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                            name="email" value={customerData.email} onChange={handleChange}
                                            placeholder="email@example.com" />
                                        {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label>Gender</label>
                                        <select className="form-control" name="gender"
                                            value={customerData.gender} onChange={handleChange}>
                                            <option value="">Select Gender</option>
                                            {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label>Date of Birth</label>
                                        <input type="date" className="form-control"
                                            name="dob" value={customerData.dob} onChange={handleChange} />
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label>Preferred Contact Method</label>
                                        <select className="form-control" name="preferred_contact_method"
                                            value={customerData.preferred_contact_method} onChange={handleChange}>
                                            <option value="">Select Method</option>
                                            {CONTACT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label>Status</label>
                                        <select className="form-control" name="status"
                                            value={customerData.status} onChange={handleChange}>
                                            {STATUSES.map(s => (
                                                <option key={s} value={s}>
                                                    {s.charAt(0).toUpperCase() + s.slice(1)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* ── Location ── */}
                                <h5 className="form-title mt-3">Location</h5>
                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <label>Country</label>
                                        <select className="form-control" name="country_id"
                                            value={customerData.country_id} onChange={handleChange}>
                                            <option value="">Select Country</option>
                                            {countries.map(c => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label>State</label>
                                        <select className="form-control" name="state_id"
                                            value={customerData.state_id} onChange={handleChange}
                                            disabled={!customerData.country_id}>
                                            <option value="">
                                                {customerData.country_id ? 'Select State' : 'Select Country First'}
                                            </option>
                                            {filteredStates.map(s => (
                                                <option key={s.id} value={s.id}>{s.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label>City</label>
                                        <select className="form-control" name="city_id"
                                            value={customerData.city_id} onChange={handleChange}
                                            disabled={!customerData.state_id}>
                                            <option value="">
                                                {customerData.state_id ? 'Select City' : 'Select State First'}
                                            </option>
                                            {filteredCities.map(c => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-12 mb-3">
                                        <label>Address</label>
                                        <textarea className="form-control" name="address" rows="2"
                                            value={customerData.address} onChange={handleChange}
                                            placeholder="Enter full address"></textarea>
                                    </div>
                                </div>

                                {/* ── Travel Documents ── */}
                                <h5 className="form-title mt-3">Travel Documents</h5>
                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <label>Passport Number</label>
                                        <input type="text" className="form-control"
                                            name="passport_number" value={customerData.passport_number}
                                            onChange={handleChange} placeholder="e.g. A1234567" />
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label>Passport Expiry Date</label>
                                        <input type="date" className="form-control"
                                            name="passport_expiry_date" value={customerData.passport_expiry_date}
                                            onChange={handleChange} />
                                    </div>
                                </div>

                                {/* ── Notes ── */}
                                <h5 className="form-title mt-3">Notes</h5>
                                <div className="row">
                                    <div className="col-md-12 mb-3">
                                        <textarea className="form-control" name="notes" rows="3"
                                            value={customerData.notes} onChange={handleChange}
                                            placeholder="Any additional notes about this customer..."></textarea>
                                    </div>
                                </div>

                                {/* ── Actions ── */}
                                <div className="text-end mt-3">
                                    <button type="button" className="btn btn-secondary me-2" onClick={handleBack}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary" disabled={isSaving}>
                                        {isSaving
                                            ? (isEdit ? 'Updating...' : 'Saving...')
                                            : (isEdit ? 'Update Customer' : 'Save Customer')}
                                    </button>
                                </div>

                            </form>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        </div>
    );
}

export default AddCustomer;